import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {Contest, ContestDto} from '../model/contest/Contest';
import {CompetitionService} from './competition.service';
import {Role} from '../model/auth/Role';
import {Region} from '../model/location/Region';
import {addPageParams, DEFAULT_PAGE, Page} from '../model/Page';
import {EnrolData, EnrolDto, EnrolledUserDto, EnrolRequestDto, Teacher, User, UserDto} from '../model/auth/User';
import {PaymentGroup, PaymentGroupDto, PaymentStatus} from '../model/Payment';
import {School, SchoolBaseDto} from '../model/location/School';

@Injectable({
  providedIn: 'root'
})
export class ContestService {

  public enrolRequestsChanged = new EventEmitter<Role>();

  constructor(private http: HttpClient) {
  }

  private static CONTEST_ID = 'CONTEST_ID';
  private static SOURCE_CONTEST_ID = 'SOURCE_CONTEST_ID';
  private static USER_ID = 'USER_ID';
  private static GROUP_ID = 'GROUP_ID';
  private static REGION_ID = 'REGION_ID';
  private static CONTEST_ROLE = 'CONTEST_ROLE';
  private static STATUS = 'STATUS';

  private static CONTESTS_URL = '/contests';

  private static COMPETITION_CONTESTS_URL = `${CompetitionService.COMPETITIONS_DETAILS_URL}${ContestService.CONTESTS_URL}`;
  private static OPEN_ENROL_CONTESTS_URL = `${ContestService.COMPETITION_CONTESTS_URL}/open`;

  private static CONTESTS_USERS_URL = `${ContestService.COMPETITION_CONTESTS_URL}/${ContestService.CONTEST_ID}/roles/${ContestService.CONTEST_ROLE}`;
  private static CONTESTS_USERS_DETAILS_URL = `${ContestService.CONTESTS_USERS_URL}/users/${ContestService.USER_ID}`;

  private static CONTESTS_TEACHERS_URL = `${ContestService.CONTESTS_USERS_URL}/teacher`;

  private static CONTESTS_PAYMENTS_BASE_URL = `/payments${ContestService.COMPETITION_CONTESTS_URL}/${ContestService.CONTEST_ID}`;
  private static CONTESTS_PAYMENT_URL = `/payments${ContestService.CONTESTS_USERS_URL}/users/${ContestService.USER_ID}`;
  private static CONTESTS_PAYMENT_GROUP_URL = `${ContestService.CONTESTS_PAYMENTS_BASE_URL}/groups/${ContestService.GROUP_ID}`;
  private static CONTESTS_PAYMENTS_LIST_URL = `${ContestService.CONTESTS_PAYMENTS_BASE_URL}/regions/${ContestService.REGION_ID}/roles/${ContestService.CONTEST_ROLE}/${ContestService.STATUS}`;

  private static CONTESTS_DETAILS_URL = `${ContestService.CONTESTS_URL}/${ContestService.CONTEST_ID}`;
  private static CONTESTS_COPY_TASK_URL = `${ContestService.CONTESTS_DETAILS_URL}/tasks/clone?source_contest_id=${ContestService.SOURCE_CONTEST_ID}`;
  private static CONTESTS_ENROL_URL = `${ContestService.CONTESTS_DETAILS_URL}/enrolments`;
  private static CONTESTS_ENROL_REQUESTS_URL = `${ContestService.CONTESTS_ENROL_URL}/requests`;

  create(competitionId: string, data: ContestDto): Observable<Contest> {
    const url = ContestService.COMPETITION_CONTESTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => Contest.parse(data as ContestDto))
      );
  }

  getContests(competitionId: string): Observable<Contest[]> {
    const url = ContestService.COMPETITION_CONTESTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as ContestDto[]).map(item => Contest.parse(item)))
      );
  }

  getEnrolledUsers(competitionId: string, contestId: string, role: Role, page: Page<never>,
                   region?: Region, school?: School, userId?: string): Observable<Page<User>> {
    const url = addPageParams(ContestService.CONTESTS_USERS_URL
        .replace(CompetitionService.COMPETITION_ID, competitionId)
        .replace(ContestService.CONTEST_ID, contestId)
        .replace(ContestService.CONTEST_ROLE, role)
      + `?${region ? `region=${region.id}` : ''}${school ? `&school=${school.id}` : ''}${userId ? `&user_id=${userId}` : ''}`, page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => ContestService.parseUsersPage(data as Page<EnrolledUserDto>))
      );
  }

  geTeachers(competitionId: string, contestId: string, regionId: string, schoolId: string): Observable<Teacher[]> {
    const page: Page<never> = {
      max_items: 20,
      asc: true
    };
    const url = addPageParams(ContestService.CONTESTS_PAYMENTS_LIST_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.REGION_ID, regionId)
      .replace(ContestService.CONTEST_ROLE, Role.Teacher)
      .replace(ContestService.STATUS, PaymentStatus.successful) + (schoolId ? `?school_id=${schoolId}` : ''), page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => {
          const page = data as Page<UserDto>;

          return page.content?.map(user => ({
            teacher_id: user.id,
            teacher_first_name: user.first_name,
            teacher_last_name: user.last_name
          })) || [];
        })
      );
  }

  assignTeacher(competitionId: string, contestId: string, teacherId: string, users: string[]): Observable<User[]> {
    const url = ContestService.CONTESTS_TEACHERS_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.CONTEST_ROLE, Role.Student);

    const data = {
      users: users,
      teacher_id: teacherId,
    };

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => (data as EnrolledUserDto[]).map(user => User.parseEnrolled(user)))
      );
  }

  getOpenForEnrolContests(competitionId: string): Observable<Contest[]> {
    const url = ContestService.OPEN_ENROL_CONTESTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as ContestDto[]).map(item => Contest.parse(item)))
      );
  }

  getDetails(id: string, optionalContent = false): Observable<Contest> {
    let url = ContestService.CONTESTS_DETAILS_URL.replace(ContestService.CONTEST_ID, id);
    if (optionalContent) url += '?optional_content=true';

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => Contest.parse(data as ContestDto))
      );
  }

  enrol(contestId: string, userId?: string, role?: Role, regionId?: string, school?: SchoolBaseDto,
        payLater?: boolean, freeEnrol?: boolean): Observable<User> {
    const url = ContestService.CONTESTS_ENROL_URL.replace(ContestService.CONTEST_ID, contestId);

    const enrolDto: EnrolDto = {};

    if (userId && role) {
      enrolDto.data = [{
        user_id: userId,
        role: role
      }];
      if (payLater) enrolDto.data[0].pay_later = payLater;
      if (freeEnrol) enrolDto.data[0].free_enrol = freeEnrol;
    } else {
      if (freeEnrol) enrolDto.free_enrol = freeEnrol;
    }

    enrolDto.region_id = regionId;
    enrolDto.school = school;

    return this.http.post(`${environment.api.host}${url}`, enrolDto)
      .pipe(
        map(data => User.parseEnrolled(data as EnrolledUserDto))
      );
  }

  enrolGroup(contestId: string, enrolData: EnrolData[], regionId?: string, school?: SchoolBaseDto): Observable<PaymentGroup> {
    const url = ContestService.CONTESTS_ENROL_URL.replace(ContestService.CONTEST_ID, contestId);
    const data: EnrolDto = {
      data: enrolData,
      region_id: regionId,
      school
    };

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => PaymentGroup.parse(data as PaymentGroupDto))
      );
  }

  updateRequest(contestId: string, userId: string, role: Role, approve: boolean): Observable<User> {
    const url = ContestService.CONTESTS_ENROL_REQUESTS_URL.replace(ContestService.CONTEST_ID, contestId);
    const data: EnrolRequestDto = {
      user_id: userId,
      role: role,
      approve: approve
    };

    return this.http.post(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => User.parse(data as UserDto)),
        tap(user => this.enrolRequestsChanged.emit(role))
      );
  }

  checkPaymentStatus(competitionId: string, contestId: string, role: Role, userId: string): Observable<User> {
    const url = ContestService.CONTESTS_PAYMENT_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.CONTEST_ROLE, role)
      .replace(ContestService.USER_ID, userId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => User.parseEnrolled(data as EnrolledUserDto))
      );
  }

  checkPaymentGroupStatus(competitionId: string, contestId: string, groupId: string): Observable<PaymentGroup> {
    const url = ContestService.CONTESTS_PAYMENT_GROUP_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.GROUP_ID, groupId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => PaymentGroup.parse(data as PaymentGroupDto))
      );
  }

  getPayments(competitionId: string, contestId: string, regionId: string, role: Role | string,
              status: PaymentStatus, schoolId?: string, page: Page<never> = DEFAULT_PAGE): Observable<Page<User>> {
    const url = addPageParams(ContestService.CONTESTS_PAYMENTS_LIST_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.REGION_ID, regionId)
      .replace(ContestService.CONTEST_ROLE, role)
      .replace(ContestService.STATUS, status) + (schoolId ? `?school_id=${schoolId}` : ''), page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => ContestService.parseUsersPage(data as Page<EnrolledUserDto>))
      );
  }

  private static parseUsersPage(page: Page<EnrolledUserDto>) {
    const content = page.content ? page.content.map(u => User.parseEnrolled(u)) : [];

    return {
      max_items: page.max_items,
      next_token: page.next_token,
      asc: page.asc,
      content: content
    };
  }

  update(id: string, data: ContestDto): Observable<Contest> {
    const url = ContestService.CONTESTS_DETAILS_URL.replace(ContestService.CONTEST_ID, id);

    return this.http.put(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => Contest.parse(data as ContestDto))
      );
  }

  cloneTasks(id: string, sourceContestId: string) {
    const url = ContestService.CONTESTS_COPY_TASK_URL
      .replace(ContestService.CONTEST_ID, id)
      .replace(ContestService.SOURCE_CONTEST_ID, sourceContestId);

    return this.http.post(`${environment.api.host}${url}`, null);
  }

  delete(id: string): Observable<any> {
    const url = ContestService.CONTESTS_DETAILS_URL.replace(ContestService.CONTEST_ID, id);

    return this.http.delete(`${environment.api.host}${url}`);
  }

  deleteUser(competitionId: string, contestId: string, role: Role, userId: string): Observable<any> {
    const url = ContestService.CONTESTS_USERS_DETAILS_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(ContestService.CONTEST_ID, contestId)
      .replace(ContestService.CONTEST_ROLE, role)
      .replace(ContestService.USER_ID, userId);

    return this.http.delete(`${environment.api.host}${url}`);
  }

}
